/**
 * Wiederverwendbare Komponenten + Hook fuer den Stammdaten-Abgleich
 * SAP ↔ MSP-Portal (ESET, ALSO). Wird sowohl von der eigenstaendigen
 * `MasterDataSyncPage` als auch inline in der `LieferantenPage` (im
 * aufgeklappten Kunden-Eintrag) verwendet.
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import api from '@/lib/api.ts'
import { Button, useToast } from '@/components/ui'

// ─── API-Typen ──────────────────────────────────────────────────────────────

export type Portal = 'eset' | 'also'
export type FieldKey = 'name' | 'email' | 'phone' | 'city' | 'zip' | 'country'

export interface FieldDiff {
  field: FieldKey
  label: string
  sapValue: string | null
  portalValue: string | null
  differs: boolean
  pushable: boolean
}

export interface PortalComparison {
  portal: Portal
  externalId: string
  externalName: string | null
  configured: boolean
  available: boolean
  error?: string
  diffCount: number
  pushableDiffCount: number
  fields: FieldDiff[]
}

export interface CustomerComparison {
  customerId: string
  customerName: string
  sapCardCode: string | null
  sapAvailable: boolean
  portals: PortalComparison[]
}

export interface SyncOverviewResponse {
  generatedAt: string
  sap: { configured: boolean; available: boolean; error?: string }
  portals: Array<{ id: Portal; configured: boolean; available: boolean; error?: string }>
  customers: CustomerComparison[]
}

export interface PushResponse {
  ok: boolean
  customerId: string
  portal: Portal
  externalId: string
  pushedFields: FieldKey[]
  skippedFields: Array<{ field: FieldKey; reason: string }>
  error?: string
}

// ─── Anzeige-Konstanten ─────────────────────────────────────────────────────

export const PORTAL_LABEL: Record<Portal, string> = {
  eset: 'ESET',
  also: 'ALSO',
}

export const PORTAL_DOT: Record<Portal, string> = {
  eset: 'bg-green-500',
  also: 'bg-blue-500',
}

function displayValue(v: string | null): string {
  if (v === null || v === undefined || v === '') return '—'
  return v
}

// ─── Hook: Uebersicht laden ──────────────────────────────────────────────────

export function useMasterDataSyncOverview() {
  return useQuery<SyncOverviewResponse>({
    queryKey: ['master-data-sync'],
    queryFn: () => api.get('/einkauf/master-data-sync').then((r) => r.data),
    retry: false,
  })
}

// ─── Hook: Push-Mutation (mit Toast + Cache-Invalidierung) ─────────────────

export function useMasterDataSyncPush() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: async (vars: { customerId: string; portal: Portal; fields?: FieldKey[] }) => {
      const res = await api.post<PushResponse>('/einkauf/master-data-sync/push', vars)
      return res.data
    },
    onSuccess: (data) => {
      if (data.ok) {
        toast({
          title: `${PORTAL_LABEL[data.portal]}-Update erfolgreich`,
          description: `Übertragene Felder: ${data.pushedFields.join(', ') || '(keine)'}`,
          tone: 'success',
        })
      } else {
        toast({
          title: `${PORTAL_LABEL[data.portal]}-Update fehlgeschlagen`,
          description: data.error ?? 'Unbekannter Fehler',
          tone: 'danger',
        })
      }
      queryClient.invalidateQueries({ queryKey: ['master-data-sync'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-overview'] })
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Unbekannter Fehler')
      toast({ title: 'Übertragung fehlgeschlagen', description: msg, tone: 'danger' })
    },
  })
}

// ─── Komponente: Diff-Panel pro Portal ──────────────────────────────────────

export interface PortalDiffPanelProps {
  customerId: string
  portal: PortalComparison
  onPushField: (field: FieldKey) => void
  onPushAll: () => void
  isPending: boolean
}

export function PortalDiffPanel({
  customerId,
  portal,
  onPushField,
  onPushAll,
  isPending,
}: PortalDiffPanelProps) {
  void customerId
  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${PORTAL_DOT[portal.portal]}`} />
          <span className="text-small font-medium text-gray-900">
            {PORTAL_LABEL[portal.portal]}
          </span>
          <span className="text-caption text-gray-500">
            ID: {portal.externalId}
            {portal.externalName ? ` · ${portal.externalName}` : ''}
          </span>
        </div>
        <Button
          variant="primary"
          size="xs"
          onClick={onPushAll}
          disabled={isPending || portal.pushableDiffCount === 0 || !portal.available}
        >
          <ArrowRight className="h-3 w-3 mr-1" />
          Alle SAP-Werte pushen ({portal.pushableDiffCount})
        </Button>
      </div>

      {!portal.configured ? (
        <div className="px-3 py-3 text-small text-gray-500">
          Portal {PORTAL_LABEL[portal.portal]} ist nicht konfiguriert.
        </div>
      ) : !portal.available ? (
        <div className="px-3 py-3 text-small text-amber-700">
          {portal.error ?? 'Detail nicht abrufbar'}
        </div>
      ) : (
        <table className="w-full text-small">
          <thead className="bg-gray-50 text-caption text-gray-500 uppercase">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Feld</th>
              <th className="text-left px-3 py-2 font-medium">SAP</th>
              <th className="text-left px-3 py-2 font-medium">
                {PORTAL_LABEL[portal.portal]}
              </th>
              <th className="text-right px-3 py-2 font-medium">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {portal.fields.map((f) => (
              <tr key={f.field} className="border-t border-gray-100">
                <td className="px-3 py-2 text-gray-700">{f.label}</td>
                <td className="px-3 py-2 text-gray-900">{displayValue(f.sapValue)}</td>
                <td className="px-3 py-2">
                  {f.differs ? (
                    <span className="text-amber-700">{displayValue(f.portalValue)}</span>
                  ) : (
                    <span className="text-gray-500">{displayValue(f.portalValue)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {!f.pushable ? (
                    <span
                      className="text-caption text-gray-400"
                      title={`Portal ${PORTAL_LABEL[portal.portal]} unterstützt das Feld nicht im Update.`}
                    >
                      nicht pushbar
                    </span>
                  ) : !f.differs ? (
                    <span className="text-caption text-gray-400">synchron</span>
                  ) : !f.sapValue ? (
                    <span className="text-caption text-gray-400">SAP leer</span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => onPushField(f.field)}
                      disabled={isPending}
                    >
                      <ArrowRight className="h-3 w-3 mr-1" />
                      Pushen
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Komponente: Komplettpanel pro Kunde ───────────────────────────────────
//
// Rendert fuer einen Kunden EINE Tabelle: Spalten = Feld + SAP + ein
// Spalten-Block pro angebundenem MSP-Portal. So sieht man auf einen Blick
// wo Diffs sind, statt pro Portal eine eigene Mini-Sektion.

export interface CustomerMasterDataSyncProps {
  comparison: CustomerComparison
}

const INLINE_FIELD_ORDER: FieldKey[] = ['name', 'email', 'phone', 'city', 'zip', 'country']
const INLINE_FIELD_LABELS: Record<FieldKey, string> = {
  name: 'Firmenname',
  email: 'E-Mail',
  phone: 'Telefon',
  city: 'Stadt',
  zip: 'PLZ',
  country: 'Land',
}
const INLINE_PORTAL_ORDER: Portal[] = ['eset', 'also']

export function CustomerMasterDataSync({ comparison }: CustomerMasterDataSyncProps) {
  const pushMutation = useMasterDataSyncPush()

  function handlePushAll(portal: Portal) {
    pushMutation.mutate({ customerId: comparison.customerId, portal })
  }
  function handlePushSingle(portal: Portal, field: FieldKey) {
    pushMutation.mutate({ customerId: comparison.customerId, portal, fields: [field] })
  }

  // Sichtbare Portal-Spalten in stabiler Reihenfolge.
  const portalsByKey = new Map<Portal, PortalComparison>()
  for (const p of comparison.portals) portalsByKey.set(p.portal, p)
  const visiblePortals: Portal[] = INLINE_PORTAL_ORDER.filter((id) => portalsByKey.has(id))

  // SAP-Werte: identisch ueber alle Portale. Wir nehmen den ersten
  // verfuegbaren — oder fallen auf null zurueck.
  function sapValueFor(field: FieldKey): string | null {
    for (const portal of visiblePortals) {
      const pf = portalsByKey.get(portal)?.fields.find((x) => x.field === field)
      if (pf) return pf.sapValue
    }
    return null
  }

  function isPendingFor(portal: Portal): boolean {
    return (
      pushMutation.isPending &&
      pushMutation.variables?.customerId === comparison.customerId &&
      pushMutation.variables?.portal === portal
    )
  }

  return (
    <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-small border-collapse">
          <thead className="bg-gray-50 text-caption text-gray-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium border-b border-gray-200">Feld</th>
              <th className="text-left px-3 py-2 font-medium border-b border-gray-200">SAP</th>
              {visiblePortals.map((portal) => {
                const p = portalsByKey.get(portal)!
                return (
                  <th
                    key={portal}
                    className="text-left px-3 py-2 font-medium border-b border-gray-200 align-top"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${PORTAL_DOT[portal]}`} />
                      <span className="uppercase tracking-wide">{PORTAL_LABEL[portal]}</span>
                      <span className="text-gray-400 normal-case">{p.externalId}</span>
                      {p.available && p.pushableDiffCount > 0 ? (
                        <Button
                          variant="primary"
                          size="xs"
                          onClick={() => handlePushAll(portal)}
                          disabled={isPendingFor(portal)}
                          className="ml-auto"
                        >
                          <ArrowRight className="h-3 w-3 mr-1" />
                          Alle ({p.pushableDiffCount})
                        </Button>
                      ) : null}
                    </div>
                    {!p.configured ? (
                      <div className="mt-1 text-caption text-gray-400 normal-case">
                        Portal nicht konfiguriert
                      </div>
                    ) : !p.available ? (
                      <div className="mt-1 text-caption text-amber-700 normal-case">
                        {p.error ?? 'Detail nicht abrufbar'}
                      </div>
                    ) : null}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {INLINE_FIELD_ORDER.map((field) => (
              <tr key={field} className="border-b border-gray-100 last:border-b-0">
                <td className="px-3 py-2 text-gray-700">{INLINE_FIELD_LABELS[field]}</td>
                <td className="px-3 py-2 text-gray-900 font-medium">
                  {sapValueFor(field) || '—'}
                </td>
                {visiblePortals.map((portal) => {
                  const p = portalsByKey.get(portal)!
                  const f = p.fields.find((x) => x.field === field)
                  return (
                    <td key={portal} className="px-3 py-2 align-top">
                      <InlineCell
                        portal={p}
                        diff={f}
                        onPush={() => handlePushSingle(portal, field)}
                        isPending={isPendingFor(portal)}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function InlineCell({
  portal,
  diff,
  onPush,
  isPending,
}: {
  portal: PortalComparison
  diff: FieldDiff | undefined
  onPush: () => void
  isPending: boolean
}) {
  if (!portal.configured || !portal.available) {
    return <span className="text-caption text-gray-400">—</span>
  }
  if (!diff) {
    return <span className="text-caption text-gray-400">—</span>
  }
  if (!diff.differs) {
    return <span className="text-caption text-gray-400">synchron</span>
  }
  if (!diff.pushable) {
    return (
      <span
        className="text-caption text-gray-400"
        title={`Portal ${PORTAL_LABEL[portal.portal]} unterstützt das Feld nicht im Update.`}
      >
        nicht pushbar
      </span>
    )
  }
  return (
    <div className="flex items-start gap-2">
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-gray-500 line-through truncate">{diff.portalValue || '—'}</span>
        <span className="text-amber-700 font-medium truncate">{diff.sapValue || '—'}</span>
      </div>
      <Button
        variant="ghost"
        size="xs"
        onClick={onPush}
        disabled={isPending}
        className="ml-auto"
        title="SAP-Wert in Portal schreiben"
      >
        <ArrowRight className="h-3 w-3" />
      </Button>
    </div>
  )
}

// ─── Helfer fuer die Konsumenten ─────────────────────────────────────────────

/**
 * Zaehlt die pushbaren Abweichungen eines Customer-Comparison-Eintrags
 * (ueber alle Portale).
 */
export function countPushableDiffs(c: CustomerComparison): number {
  return c.portals.reduce((s, p) => s + p.pushableDiffCount, 0)
}

/**
 * Sequentiell alle Portale eines Kunden pushen, die Diffs haben.
 * Liefert ein Ergebnis-Array zurueck, damit der Aufrufer pro Portal Erfolg
 * oder Fehler anzeigen kann (z. B. im Bulk-Click-Thru-Modal).
 */
export async function pushAllPortalsForCustomer(
  c: CustomerComparison,
): Promise<Array<{ portal: Portal; result: PushResponse | { ok: false; error: string } }>> {
  const out: Array<{ portal: Portal; result: PushResponse | { ok: false; error: string } }> = []
  for (const p of c.portals) {
    if (p.pushableDiffCount === 0 || !p.available) continue
    try {
      const res = await api.post<PushResponse>('/einkauf/master-data-sync/push', {
        customerId: c.customerId,
        portal: p.portal,
      })
      out.push({ portal: p.portal, result: res.data })
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Unbekannter Fehler')
      out.push({ portal: p.portal, result: { ok: false, error: msg } })
    }
  }
  return out
}

// ─── Hook: Bulk-Push (Preview → Confirm → Run) ──────────────────────────────

export interface BulkPushItemResult {
  customerId: string
  customerName: string
  portals: Array<{ portal: Portal; result: PushResponse | { ok: false; error: string } }>
}

export type BulkPushPhase = 'preview' | 'running' | 'done'

/** Schluessel fuer das Selection-Set: `customerId|portal|field`. */
function selectionKey(customerId: string, portal: Portal, field: FieldKey): string {
  return `${customerId}|${portal}|${field}`
}

export interface BulkPushState {
  isOpen: boolean
  phase: BulkPushPhase
  /** Vorschau-Liste der zu uebertragenden Kunden + Portal-Diffs. */
  preview: CustomerComparison[]
  total: number
  doneCount: number
  current: { customerId: string; customerName: string } | null
  results: BulkPushItemResult[]
  /** Set der **abgewaehlten** (customerId, portal, field)-Tripel. Standard
   *  ist „alle pushbaren Diffs an"; durch Klick auf eine Checkbox in der
   *  Preview-Tabelle landet das Tripel in diesem Set. */
  deselected: Set<string>
  /** Hilfsfunktion: ist dieses (customer, portal, field) noch ausgewaehlt? */
  isSelected(customerId: string, portal: Portal, field: FieldKey): boolean
  /** Toggle eines (customer, portal, field). */
  toggle(customerId: string, portal: Portal, field: FieldKey): void
  /** Anzahl der noch selektierten pushbaren Diffs (fuer Zaehler im Footer). */
  selectedCount: number
  /** Modal mit Vorschau oeffnen, Push startet erst bei `confirm()`. */
  open(customers: CustomerComparison[]): void
  /** Vorschau bestaetigen — startet die sequentielle Uebertragung. */
  confirm(): Promise<void>
  /** Modal schliessen. Waehrend running gesperrt. */
  close(): void
}

/**
 * Hook fuer den Bulk-Click-Thru mit Preview-Phase und Per-Feld-Auswahl.
 *
 * Workflow:
 *   1. `open(customers)` → Modal im Phase `preview`, Tabelle zeigt pro
 *      Kunde + Feld + Portal Checkboxen. Default: alle aktivierten Diffs.
 *   2. User schaltet einzelne Felder ab (Checkbox).
 *   3. `confirm()` baut pro (Kunde, Portal) die Liste der ausgewaehlten
 *      Felder und schickt nur diese an den Push-Endpoint.
 */
export function useBulkMasterDataSyncPush(): BulkPushState {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [phase, setPhase] = useState<BulkPushPhase>('preview')
  const [preview, setPreview] = useState<CustomerComparison[]>([])
  const [total, setTotal] = useState(0)
  const [doneCount, setDoneCount] = useState(0)
  const [current, setCurrent] = useState<{ customerId: string; customerName: string } | null>(null)
  const [results, setResults] = useState<BulkPushItemResult[]>([])
  const [deselected, setDeselected] = useState<Set<string>>(new Set())

  const open = useCallback((customers: CustomerComparison[]) => {
    if (customers.length === 0) return
    setPreview(customers)
    setTotal(customers.length)
    setDoneCount(0)
    setCurrent(null)
    setResults([])
    setDeselected(new Set())
    setPhase('preview')
    setIsOpen(true)
  }, [])

  const isSelected = useCallback(
    (customerId: string, portal: Portal, field: FieldKey) => {
      return !deselected.has(selectionKey(customerId, portal, field))
    },
    [deselected],
  )

  const toggle = useCallback(
    (customerId: string, portal: Portal, field: FieldKey) => {
      const key = selectionKey(customerId, portal, field)
      setDeselected((prev) => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    },
    [],
  )

  // Zaehler: alle pushbaren Diffs der Vorschau, die noch ausgewaehlt sind.
  const selectedCount = preview.reduce((sum, c) => {
    for (const p of c.portals) {
      for (const f of p.fields) {
        if (!f.differs || !f.pushable) continue
        if (!deselected.has(selectionKey(c.customerId, p.portal, f.field))) sum++
      }
    }
    return sum
  }, 0)

  const confirm = useCallback(async () => {
    if (preview.length === 0) return
    setPhase('running')
    let okCount = 0
    let errCount = 0
    for (const c of preview) {
      setCurrent({ customerId: c.customerId, customerName: c.customerName })
      const portalResults: BulkPushItemResult['portals'] = []
      for (const p of c.portals) {
        if (!p.available) continue
        const fieldsToPush = p.fields
          .filter((f) => f.differs && f.pushable)
          .map((f) => f.field)
          .filter((f) => !deselected.has(selectionKey(c.customerId, p.portal, f)))
        if (fieldsToPush.length === 0) continue
        try {
          const res = await api.post<PushResponse>('/einkauf/master-data-sync/push', {
            customerId: c.customerId,
            portal: p.portal,
            fields: fieldsToPush,
          })
          portalResults.push({ portal: p.portal, result: res.data })
          if (res.data.ok) okCount++
          else errCount++
        } catch (err) {
          const msg =
            (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            (err instanceof Error ? err.message : 'Unbekannter Fehler')
          portalResults.push({ portal: p.portal, result: { ok: false, error: msg } })
          errCount++
        }
      }
      setResults((prev) => [
        ...prev,
        { customerId: c.customerId, customerName: c.customerName, portals: portalResults },
      ])
      setDoneCount((d) => d + 1)
    }
    setCurrent(null)
    setPhase('done')
    queryClient.invalidateQueries({ queryKey: ['master-data-sync'] })
    queryClient.invalidateQueries({ queryKey: ['supplier-overview'] })
    toast({
      title: 'Bulk-Push abgeschlossen',
      description: `${okCount} Portal-Update(s) erfolgreich, ${errCount} fehlgeschlagen.`,
      tone: errCount === 0 ? 'success' : 'warning',
    })
  }, [preview, deselected, queryClient, toast])

  const close = useCallback(() => {
    if (phase !== 'running') setIsOpen(false)
  }, [phase])

  return {
    isOpen,
    phase,
    preview,
    total,
    doneCount,
    current,
    results,
    deselected,
    isSelected,
    toggle,
    selectedCount,
    open,
    confirm,
    close,
  }
}
